import { useState, useEffect, useRef } from 'react';
import { documentsApi, Document, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// File type icon component
function FileIcon({ fileType }: { fileType: string }) {
  if (fileType.startsWith('image/')) {
    return (
      <svg className="w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (fileType === 'application/pdf') {
    return (
      <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (fileType.includes('word') || fileType.includes('document')) {
    return (
      <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentLibraryPage() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hasBookingAccess, setHasBookingAccess] = useState(true);

  // Upload modal state
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDisplayName, setUploadDisplayName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadRequiresBooking, setUploadRequiresBooking] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    loadDocuments();
    loadCategories();
  }, [categoryFilter]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await documentsApi.list(
        categoryFilter ? { category: categoryFilter } : undefined
      );
      setDocuments(result.documents);
      setHasBookingAccess(result.hasBookingAccess);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const result = await documentsApi.getCategories();
      setCategories(result.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleDownload = (doc: Document) => {
    window.open(documentsApi.getDownloadUrl(doc.id), '_blank');
  };

  const handleDelete = async (doc: Document) => {
    if (!confirm(`Delete "${doc.displayName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await documentsApi.delete(doc.id);
      setDocuments(documents.filter((d) => d.id !== doc.id));
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to delete document');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadDisplayName(file.name);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadDisplayName(file.name);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      await documentsApi.upload({
        file: uploadFile,
        displayName: uploadDisplayName || undefined,
        description: uploadDescription || undefined,
        category: uploadCategory || undefined,
        requiresBooking: uploadRequiresBooking,
      });

      // Reload documents and categories
      await loadDocuments();
      await loadCategories();

      // Reset and close modal
      resetUploadForm();
      setShowUploadModal(false);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setUploadError(err.message);
      } else {
        setUploadError('Failed to upload document');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadDisplayName('');
    setUploadDescription('');
    setUploadCategory('');
    setUploadRequiresBooking(false);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Helpful documents and resources for your stay.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowUploadModal(true)} className="btn-primary">
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Document
          </button>
        )}
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setCategoryFilter('')}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              !categoryFilter
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                categoryFilter === cat
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {!hasBookingAccess && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-md">
          Some documents require an active booking to access.
        </div>
      )}

      {/* Documents grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : documents.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No Documents</h3>
          <p className="mt-2 text-sm text-gray-500">
            {isAdmin
              ? 'Upload documents to share with guests.'
              : 'No documents have been uploaded yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="card p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3">
                <FileIcon fileType={doc.fileType} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">
                    {doc.displayName}
                  </h3>
                  {doc.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                      {doc.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                    <span>{formatFileSize(doc.fileSize)}</span>
                    {doc.category && (
                      <>
                        <span>|</span>
                        <span>{doc.category}</span>
                      </>
                    )}
                    {doc.requiresBooking && (
                      <>
                        <span>|</span>
                        <span className="text-amber-500">Booking required</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleDownload(doc)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Download
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(doc)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => {
              resetUploadForm();
              setShowUploadModal(false);
            }}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Upload Document
                </h2>

                {uploadError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm mb-4">
                    {uploadError}
                  </div>
                )}

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
                  />
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileIcon fileType={uploadFile.type} />
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {uploadFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(uploadFile.size)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">
                        Drop a file here or click to browse
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        Images, PDF, Word, or text files up to 10MB
                      </p>
                    </>
                  )}
                </div>

                {/* Form fields */}
                <div className="space-y-4 mt-4">
                  <div>
                    <label htmlFor="displayName" className="label">
                      Display Name
                    </label>
                    <input
                      id="displayName"
                      type="text"
                      value={uploadDisplayName}
                      onChange={(e) => setUploadDisplayName(e.target.value)}
                      className="input"
                      placeholder="Enter a friendly name"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="label">
                      Description (optional)
                    </label>
                    <textarea
                      id="description"
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      className="input"
                      rows={2}
                      placeholder="Brief description of the document"
                    />
                  </div>

                  <div>
                    <label htmlFor="category" className="label">
                      Category (optional)
                    </label>
                    <input
                      id="category"
                      type="text"
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="input"
                      placeholder="e.g., Appliances, Building Info"
                      list="categories"
                    />
                    <datalist id="categories">
                      {categories.map((cat) => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="requiresBooking"
                      type="checkbox"
                      checked={uploadRequiresBooking}
                      onChange={(e) => setUploadRequiresBooking(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="requiresBooking" className="text-sm text-gray-700">
                      Requires active booking to access
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      resetUploadForm();
                      setShowUploadModal(false);
                    }}
                    className="btn-secondary"
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!uploadFile || isUploading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
